import { Color3, MeshBuilder, StandardMaterial } from "@babylonjs/core";
import { HeroId, Slot, ck, fxk, hv, pk } from "@rivalwatch/shared";
import { bell, click, mallet, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, ConstructFrame, ProjectileFrame } from "../types.ts";

const V = { blockadeCharge: 0, bondTicks: 1 } as const;
const PK_ACORN = pk(HeroId.SquirrelyGirl, 0);
const PK_BLOCKADE = pk(HeroId.SquirrelyGirl, 1);
const CK_WAVE = ck(HeroId.SquirrelyGirl, 0);
const FX_ACORN = fxk(HeroId.SquirrelyGirl, 0);
const FX_SQUIRRELS = fxk(HeroId.SquirrelyGirl, 1);
const cue = (db: number, f: Cue) => trim(db, f);
const acornFire = cue(13.6, (o) => {
  click(o, 0.07, 2400, 0.004);
  thump(o, 0.08, 260, 130, 0.05);
  tone(o, { f: 740, f1: 520, glide: 0.08, gain: 0.12, d: 0.18 });
  noise(o, { color: "pink", type: "bandpass", f: 950, f1: 1500, gain: 0.08, d: 0.15 });
  return 0.26;
});
const blockade = cue(12, (o) => {
  whoosh(o, 0.24, 400, 1600, 0.22);
  mallet(o, 880, 0.12, 0.5);
  return 0.32;
});
const bounce = cue(9, (o) => {
  thump(o, 0.32, 130, 64, 0.12);
  bell(o, 1320, 0.1, 0.3);
  return 0.32;
});
const pact = cue(10, (o) => {
  [0, 4, 7].forEach((s, i) => mallet({ ...o, t: o.t + i * 0.055 }, 293.66 * 2 ** (s / 12), 0.1, 0.45));
  return 0.5;
});
const surge = cue(4, (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 2500, gain: 0.4, d: 0.7 });
  thump(o, 0.5, 95, 40, 0.2);
  return 1;
});
const reloadAcorns = (seconds: number): Cue =>
  cue(8, (o) => {
    for (let i = 0; i < 4; i++) {
      const at = (0.18 + i * 0.13) * seconds;
      click({ ...o, t: o.t + at }, 0.006, 1800 + i * 250, 0.006);
      thump({ ...o, t: o.t + at + 0.015 }, 0.006, 360 + i * 30, 160, 0.04);
    }
    noise(o, { color: "pink", type: "bandpass", f: 700, f1: 1700, gain: 0.32, d: seconds * 0.72, delay: seconds * 0.12 });
    tone(o, { f: 392, f1: 660, glide: seconds * 0.65, gain: 0.045, d: seconds * 0.72, delay: seconds * 0.12 });
    mallet({ ...o, t: o.t + seconds * 0.82 }, 880, 0.12, 0.03);
    return seconds + 0.2;
  });

const theme = {
  bpm: 148,
  octave: 1,
  voice: mallet,
  notes: [
    [0, 0.25],
    [4, 0.25],
    [7, 0.25],
    [9, 0.25],
    [12, 0.5],
    [7, 0.5],
  ],
} satisfies ClientHero["audio"]["theme"];

function acornView(scene: Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], f: ProjectileFrame, blockadeShot = false) {
  const acorn = MeshBuilder.CreateSphere(blockadeShot ? "blockade-acorn" : "burst-acorn", { diameter: blockadeShot ? 0.44 : 0.32, segments: 10 }, scene);
  const mat = new StandardMaterial(blockadeShot ? "blockade-acorn-mat" : "burst-acorn-mat", scene);
  mat.diffuseColor = Color3.FromHexString(blockadeShot ? "#fff2d8" : "#8b5a2b");
  mat.emissiveColor = Color3.FromHexString(blockadeShot ? "#f0c27a" : "#5a3218").scale(0.35);
  acorn.material = mat;
  acorn.isPickable = false;
  return {
    gear: { acorn },
    update(g: { acorn: typeof acorn }, frame: ProjectileFrame) {
      g.acorn.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
      g.acorn.rotation.x += frame.dt * frame.speed;
    },
    dispose() {
      mat.dispose();
      acorn.dispose();
    },
  };
}

const hero: ClientHero = {
  id: HeroId.SquirrelyGirl,
  bulk: 0.95,
  shape: { head: 1.08, torso: 0.95, arms: 0.92, legs: 1.05 },
  gear(g) {
    const { scene, add, rig, toon, glow, R, H } = g;
    for (const side of [-1, 1]) {
      const ear = add(MeshBuilder.CreateSphere("squirrel-ear", { diameter: R * 0.55, segments: 8 }, scene), rig.head, toon("#8b5a2b"));
      ear.position.set(side * R * 0.62, R * 0.75, 0.05);
      ear.scaling.y = 1.35;
    }
    const tailMat = toon("#7a4018");
    const tail = [0, 1, 2, 3].map((i) => {
      const seg = add(MeshBuilder.CreateSphere("squirrel-bushy-tail", { diameter: H * (0.22 + i * 0.025), segments: 10 }, scene), rig.torso, tailMat);
      seg.position.set(0, 0.02 + i * H * 0.11, 0.42 + i * H * 0.03);
      seg.scaling.set(1.2 - i * 0.08, 0.9, 0.72);
      return seg;
    });
    const pouch = add(MeshBuilder.CreateBox("acorn-pouch", { width: 0.32, height: 0.26, depth: 0.16 }, scene), rig.torso, toon("#3a2110"));
    pouch.position.set(-R * 0.8, -0.05, -0.18);
    const charm = add(MeshBuilder.CreateSphere("pact-glow", { diameter: 0.16, segments: 8 }, scene), rig.handL, glow("#f0c27a"));
    for (const side of [-1, 1]) {
      const cheek = add(MeshBuilder.CreateSphere("squirrel-cheek", { diameter: R * 0.3, segments: 8 }, scene), rig.head, toon("#f1c781"));
      cheek.position.set(side * R * 0.32, -R * 0.2, -R * 0.74);
      cheek.scaling.z = 0.35;
    }
    for (const side of [-1, 1]) {
      const tooth = add(MeshBuilder.CreateBox("squirrel-buck-tooth", { width: 0.055, height: 0.2, depth: 0.035 }, scene), rig.head, toon("#fff4d2"));
      tooth.position.set(side * R * 0.06, -R * 0.35, -R * 0.82);
    }
    return { tail, charm };
  },
  animate(gear, f) {
    const g = gear as { tail: ReturnType<typeof MeshBuilder.CreateSphere>[]; charm: ReturnType<typeof MeshBuilder.CreateSphere> };
    const wag = Math.sin(f.t * (6 + f.speed * 0.5)) * (0.12 + Math.min(0.25, f.speed * 0.02));
    for (const [i, seg] of g.tail.entries()) {
      seg.rotation.x = Math.PI / 5 + i * 0.16;
      seg.rotation.z = wag * (1 + i * 0.2) + (f.action === 4 ? 0.35 : 0);
      seg.position.x = Math.sin(f.t * 5 + i) * 0.02;
    }
    g.charm.scaling.setAll(1 + (f.v[V.bondTicks] > 0 ? 0.5 : 0) + Math.sin(f.t * 12) * 0.08);
    if (f.action === 4 || f.action === 5) return "interact-right";
  },
  hud(c, eid) {
    return [
      { label: "Blockade charge", value: hv(c, eid, V.blockadeCharge) / 1.5, color: "#fff2d8" },
      { label: "Mammal Pact", value: hv(c, eid, V.bondTicks) / 300, color: "#f0c27a" },
    ];
  },
  projectiles: {
    [PK_ACORN]: (scene, f) => acornView(scene, f, false),
    [PK_BLOCKADE]: (scene, f) => acornView(scene, f, true),
  },
  constructs: {
    [CK_WAVE]: (scene, _frame: ConstructFrame) => {
      const root = MeshBuilder.CreateBox("squirrel-horde", { width: 5, height: 1.75, depth: 3 }, scene);
      const bodyMat = new StandardMaterial("squirrel-horde-mat", scene);
      bodyMat.diffuseColor = Color3.FromHexString("#8b5a2b");
      bodyMat.emissiveColor = Color3.FromHexString("#b87333").scale(0.25);
      root.material = bodyMat;
      root.isPickable = false;
      const heads = [-1.6, -0.8, 0, 0.8, 1.6].map((x) => {
        const h = MeshBuilder.CreateSphere("squirrel-head", { diameter: 0.5, segments: 8 }, scene);
        h.material = bodyMat;
        h.parent = root;
        h.position.set(x, 0.75, -0.35 + Math.abs(x) * 0.1);
        return h;
      });
      type HordeGear = { root: typeof root; heads: typeof heads };
      return {
        gear: { root, heads },
        update(g: HordeGear, fr) {
          g.root.position.set(fr.pos.x, fr.pos.y + 0.875, fr.pos.z);
          g.root.rotation.y = fr.yaw;
          const bob = Math.sin(fr.ticks * 0.4) * 0.08;
          for (const [i, h] of g.heads.entries()) h.position.y = 0.75 + bob * (i % 2 ? -1 : 1);
          g.root.visibility = 0.35 + fr.hpFrac * 0.55;
        },
        dispose() {
          for (const h of heads) h.dispose();
          bodyMat.dispose();
          root.dispose();
        },
      };
    },
  },
  fx: {
    [FX_ACORN]: {
      explosion(c, pos, radius) {
        const puff = MeshBuilder.CreateSphere("acorn-pop", { diameter: radius * 2, segments: 12 }, c.scene);
        puff.material = c.glow("#f0c27a", 0.38);
        puff.position.set(pos.x, pos.y, pos.z);
        c.timed(puff, 0.32, (t, m) => (m.scaling.setAll(0.25 + t)));
      },
    },
    [FX_SQUIRRELS]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("squirrel-hit", { diameter: radius * 2, thickness: 0.12, tessellation: 24 }, c.scene);
        ring.material = c.glow("#b87333", 0.45);
        ring.position.set(pos.x, pos.y + 0.05, pos.z);
        c.timed(ring, 0.35, (t, m) => (m.scaling.setAll(0.35 + t)));
      },
    },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:squirrely:blockade" : "fire:squirrely:acorn", cue: ctx?.alt ? blockade : acornFire }),
    reload: reloadAcorns,
    ability: { [Slot.Secondary]: blockade, [Slot.Ability1]: bounce, [Slot.Ability2]: pact, [Slot.Ultimate]: surge },
    theme,
    themeTrim: { heroic: 1, hostile: -5.8, tease: 3 },
  },
  icons: {
    [Slot.Primary]: ["M7 14c0-4 3-7 5-10 2 3 5 6 5 10", "M8 14h8", "M10 18h4"],
    [Slot.Secondary]: ["M12 4c4 3 6 6 6 10", "M6 14c0-4 2-7 6-10", "M6 14h12", "M9 18h6"],
    [Slot.Ability1]: ["M12 20V5", "M7 10l5-5 5 5", "M5 18c4-3 10-3 14 0"],
    [Slot.Ability2]: ["M5 15c3-6 11-6 14 0", "M7 9l3 3-3 3", "M17 9l-3 3 3 3"],
    [Slot.Ultimate]: ["M3 16c5-8 13-8 18 0", "M5 18c4 3 10 3 14 0", "M8 13l2 2M14 13l2 2"],
    passive: ["M8 18c-2-5 2-11 6-14", "M14 4c3 5 2 11-3 15", "M7 20h9"],
  },
};

export default hero;
